import React, {useState, useEffect, useRef} from 'react';
import {useForm} from 'react-hook-form';
import {useLocation, useNavigate} from 'react-router-dom';
import {useTitle} from '../../../components/General/Layout';
import LoadingWheel from '../../../components/General/LoadingWheel';
import MaintenanceHeader from '../../../components/Surveys/SurveyMaintenanceHeader';
import ErrorHelper from '../../../service/ErrorHelper';
import {SMaster, Survey} from '../../../service/Urls';
import {get, post} from '../../../service/ApiService';
import SatisfactionSection from '../../../components/Surveys/SatisfactionSection';
import {Box, Flex, Heading, Spacer, Textarea, Stack} from '@chakra-ui/react';
import SurveySideBar from '../../../components/Surveys/SurveySideBar';
import FlexCard from '../../../components/General/FlexCard';
import SatisfactionToTen from '../../../components/Surveys/SatisfactionToTen';
import {CustomCheckbox, CustomInput, CustomIntRadioGroup} from '../../../components/General/FormInputs';
import BoxCard from '../../../components/General/BoxCard';
import {formatDate} from '../../../helpers/Functions';

const PreNeedMaintenance = () => {
  const [data, setData] = useState({});
  const [smasterData, setSmasterData] = useState({});
  const [toast, setToast] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUpdate, setIsUpdate] = useState(true);
  const [ids, setIds] = useState([]);

  const [title, setTitle] = useTitle('Cremation Survey Maintenance');

  const isMounted = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const {
    register,
    handleSubmit,
    control,
    reset,
    getValues,
    formState: {errors},
  } = useForm();

  useEffect(() => {
    isMounted.current = true;

    loadData(location?.state?.id);

    return () => {
      isMounted.current = false;
    };
  }, []);

  const loadData = async masterId => {
    const data = await get(Survey.GET + `/` + masterId);
    setToast(data);
    if (!data.error) {
      if (!data.dateAdded) {
        data.dateAdded = localStorage.getItem('prevDateAdded') || new Date().toISOString().split('T')[0];
        setIsUpdate(false);
      } else {
        // For a datepicker to default a value it must be in yyyy-mm-dd.  Modify initial value then call reset on the data to get that defaulted
        data.dateAdded = data.dateAdded.split('T')[0];
      }
      setData(data);
      reset(data);
      const smasterResponse = await get(SMaster.RECORD + '/' + masterId);
      setToast(smasterResponse);
      if (!smasterResponse.error) {
        smasterResponse['contractDate'] = formatDate(smasterResponse.smCondate);
        setSmasterData(smasterResponse);
      }
    }
    setIsLoading(false);
    setTitle('PreNeed Survey Maintenance');
    assignIds();
  };

  // ids for the good/excellent/na questions for the buttons to auto assign
  const assignIds = () => {
    let arr = [];
    const prompts = [prompt4, prompt5, prompt7, prompt8];
    prompts.forEach(promptArr => {
      arr = arr.concat(promptArr.map(prompt => prompt.id));
    });
    setIds(arr);
  };

  const submitSurvey = async formData => {
    formData.svRecord = data.svRecord ?? smasterData.smRecord;
    formData.svClient = data.svClient ?? smasterData.smClient;
    formData.svLoc = data.svLoc ?? smasterData.smLoc;
    formData.svGroup = data.svGroup ?? smasterData.smGroup;

    formData.clientId = smasterData.nclientpk;
    formData.groupId = smasterData.ngrouppk;
    formData.locationId = smasterData.locationpk;
    formData.sMasterId = smasterData.npk;

    formData.svPos = data.svPos;
    formData.svNeg = data.svNeg;
    formData.svNeedatt = data.svNeedatt;

    let url = Survey.ADD_PRE_NEED;
    let successMessage = 'Successfully added survey for ' + smasterData.clientName;
    if (isUpdate) {
      url = Survey.UPDATE_PRE_NEED + '/' + formData.id;
      successMessage = 'Successfully updated survey for ' + smasterData.clientName;
    }

    setIsSaving(true);

    const response = await post(url, formData);

    if (isMounted.current === false) return;

    setIsSaving(false);

    setToast({
      response: response,
      successMessage: successMessage,
      onSuccess: () => navigate('/surveys/lookup', {state: {serviceType: location?.state?.serviceType}}),
    });
  };

  const question3 = [
    {key: 'Self', value: 1},
    {key: 'Other', value: 2},
    {key: 'Both', value: 3},
    {key: 'N/A', value: 4},
  ];

  const prompt4 = [
    {prompt: '4a. Response', id: 'sv4a'},
    {prompt: 'b. Appearance', id: 'sv4b'},
    {prompt: 'c. Professionalism', id: 'sv4c'},
  ];

  const prompt5 = [
    {prompt: '5a. Needs', id: 'sv5a'},
    {prompt: 'b. Wishes', id: 'sv5b'},
    {prompt: 'c. Detail', id: 'sv5c'},
    {prompt: 'd. Knowledgeable', id: 'sv5d'},
    {prompt: 'e. Sincere', id: 'sv5e'},
    {prompt: 'f. Answers', id: 'sv5f'},
    {prompt: 'g. Prices/Options', id: 'sv5g'},
    {prompt: 'h. Payment Options', id: 'sv5h'},
    {prompt: 'i. Tour Facilities', id: 'sv5j'},
  ];

  const prompt7 = [
    {prompt: '7a. Accurate Paperwork', id: 'sv7a'},
    {prompt: 'b. Timely Completed', id: 'sv7b'},
    {prompt: 'c. Available', id: 'sv7c'},
  ];

  const prompt8 = [{prompt: '8a. Satisfaction', id: 'sv8a'}];

  const YesNoNa = [
    {key: 'Yes', value: 1},
    {key: 'No', value: 2},
    {key: 'N/A', value: 3},
  ];

  return (
    <>
      {isLoading ? (
        <>
          <LoadingWheel />
          <ErrorHelper toastResponse={toast} />
        </>
      ) : (
        <form onSubmit={handleSubmit(submitSurvey)}>
          <Box w={{base: '100%', lg: '90%'}}>
            <MaintenanceHeader
              register={register}
              control={control}
              errors={errors}
              masterData={smasterData}
              data={data}
              noDeceased
              setData={setData}
              reset={reset}
              ids={ids}
              getValues={getValues}
              submit={handleSubmit(submitSurvey)}
            />

            <Flex>
              <Box w="40%">
                <BoxCard textAlign="start">
                  <Flex>
                    <Heading size="sm" mr="4">
                      1.
                    </Heading>
                    <Box textAlign="start">
                      <CustomCheckbox name="Referral" id={'sv1a'} control={control} defaultChecked={data.sv1a} />
                      <CustomCheckbox name="Location" id={'sv1c'} control={control} defaultChecked={data.sv1c} />
                      <CustomCheckbox name="Website" id={'sv1j'} control={control} defaultChecked={data.sv1j} />
                      <CustomCheckbox name="Recommendation" id={'sv1b'} control={control} defaultChecked={data.sv1b} />
                    </Box>
                    <Spacer />
                    <Box textAlign="start">
                      <CustomCheckbox name="Reputation" id={'sv1d'} control={control} defaultChecked={data.sv1d} />
                      <CustomCheckbox name="Previous Experience" id={'sv1e'} control={control} defaultChecked={data.sv1e} />
                      <CustomCheckbox name="Advertising" id={'sv1f'} control={control} defaultChecked={data.sv1f} />
                      <CustomCheckbox name="Seminar" id={'sv1g'} control={control} defaultChecked={data.sv1g} />
                    </Box>
                  </Flex>
                  <Textarea rows={1} id="sv1bd" name="Recommendation" {...register('sv1bd')} errors={errors} />
                  <CustomCheckbox name="Other" id={'sv1h'} control={control} defaultChecked={data.sv1h} ml="7" />
                  <Textarea rows={1} id="sv1hd" name="Other" {...register('sv1hd')} errors={errors} />
                </BoxCard>

                <BoxCard>
                  <Flex>
                    <Heading size="sm" mr="4">
                      2.
                    </Heading>
                    <Box textAlign="start">
                      <CustomCheckbox name="At Our Office" control={control} id="sv2a" defaultChecked={data.sv2a} />
                      <CustomCheckbox name="In Your Home" control={control} id="sv2b" defaultChecked={data.sv2b} />
                      <CustomCheckbox name="Other" control={control} id="sv2e" defaultChecked={data.sv2e} />
                    </Box>
                    <Spacer />
                    <Box textAlign="start" mr="9">
                      <CustomCheckbox name="Via Telephone" control={control} id="sv2c" defaultChecked={data.sv2c} />
                      <CustomCheckbox name="Via Internet" control={control} id="sv2d" defaultChecked={data.sv2d} />
                    </Box>
                  </Flex>
                  <Textarea rows={1} id="sv2ed" name="Other" {...register('sv2ed')} errors={errors} />
                </BoxCard>

                <FlexCard>
                  <Heading size="sm" mr="4">
                    3.
                  </Heading>
                  <CustomIntRadioGroup list={question3} control={control} id={'sv3a'} errors={errors} defaultValue={data.sv3a} />
                </FlexCard>

                <SatisfactionSection control={control} prompts={prompt4} data={data} />

                <SatisfactionSection control={control} prompts={prompt5} data={data} />
              </Box>

              <Box w="40%">
                <BoxCard textAlign="start">
                  <Flex>
                    <Heading size="sm" mr="4">
                      6. PrePayment
                    </Heading>
                    <CustomIntRadioGroup list={YesNoNa} control={control} id={'sv6a'} errors={errors} defaultValue={data.sv6a} />
                  </Flex>
                  <Textarea rows={1} id="sv6ad" name="Other" {...register('sv6ad')} errors={errors} />
                </BoxCard>

                <SatisfactionSection control={control} prompts={prompt7} data={data} discardHeader />
                <SatisfactionSection control={control} prompts={prompt8} data={data} discardHeader />
                <SatisfactionToTen control={control} question={{prompt: '9a. Recommendation', id: 'sv9a'}} data={data} />

                <FlexCard>
                  <Stack width="100%">
                    <Flex>
                      <Heading size="sm">10. Preplan Info</Heading>
                      <CustomIntRadioGroup list={YesNoNa} control={control} id={'sv10a1'} errors={errors} defaultValue={data.sv10a1} />
                    </Flex>

                    <Box pt={3}>
                      <Heading size="sm" pb={3}>
                        Name & Phone
                      </Heading>
                      <Flex>
                        <Box flex={1}>
                          <CustomInput name="Name1" id="sv10a" register={register} errors={errors} />
                          <CustomInput name="Name2" id="sv10c" register={register} errors={errors} />
                          <CustomInput name="Name3" id="sv10e" register={register} errors={errors} />
                        </Box>
                        <Box flex={1}>
                          <CustomInput name="Phone1" id="sv10b" register={register} errors={errors} />
                          <CustomInput name="Phone2" id="sv10d" register={register} errors={errors} />
                          <CustomInput name="Phone3" id="sv10f" register={register} errors={errors} />
                        </Box>
                      </Flex>
                    </Box>
                  </Stack>
                </FlexCard>

                <FlexCard>
                  <Stack alignItems="flex-start" width="100%">
                    <Heading size="sm">11. Group/Club</Heading>
                    <Textarea rows={1} id="sv11a" name="Other" {...register('sv11a')} errors={errors} />
                    <Textarea rows={1} id="sv11b" name="Other" {...register('sv11b')} errors={errors} />
                  </Stack>
                </FlexCard>
              </Box>

              <Box w="20%">
                <SurveySideBar
                  control={control}
                  includes={{chinese: false, surveyFormat: false, recommendations: true}}
                  data={data}
                  register={register}
                  errors={errors}
                  svcType={location?.state?.serviceType}
                  isSaving={isSaving}
                />
              </Box>
            </Flex>

            <FlexCard>
              <Stack alignItems="flex-start" spacing={3} width="100%">
                <Heading size="sm">Comments</Heading>
                <Textarea rows={3} id="svComment" name="svComment" {...register('svComment')} errors={errors} />
              </Stack>
            </FlexCard>
          </Box>
          <ErrorHelper toastResponse={toast} />
        </form>
      )}
    </>
  );
};

export default PreNeedMaintenance;
